import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import { getDemoShareSrc, DEMO_SRC_EVENT } from "./demoSource";
import FlagshipFeedPhone from "./FlagshipFeedPhone";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * FlagshipTwoPhones — the Letter-K "two phones" experience, reused on the flagship home.
 * LEFT: the REAL editor (the /s/ share on a 496px viewport = its true mobile layout), where you tick
 * clips. We poll the same-origin iframe for checked rows (in pick order — the ET renders 1st/2nd…
 * ordinals and ids its <video> as v-<clipId>) and hand that ordered id list to the RIGHT phone,
 * FlagshipFeedPhone, which plays your picks back-to-back as one swipeable reel (with reframe).
 */

const CHERRY = "#ec2b57";
const F = "Outfit, system-ui, -apple-system, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, monospace";

const ET_VIEWPORT_W = 496; // just under the ET's 500px xs breakpoint = full mobile layout, no clipping
const FRAME_W = 300;
const FRAME_H = 600;
const ET_SCALE = FRAME_W / ET_VIEWPORT_W;

const PhoneFrame: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Box sx={{ flexShrink: 0, p: "10px", borderRadius: "44px", background: "linear-gradient(160deg,#2a2733,#141219)", border: "1px solid rgba(255,255,255,0.14)", boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 90px -30px ${CHERRY}66` }}>
    <Box sx={{ position: "relative", boxSizing: "content-box", width: FRAME_W, height: FRAME_H, borderRadius: "34px", overflow: "hidden", background: "#0b0a0f" }}>
      {children}
    </Box>
  </Box>
);

const label = (text: string, color: string) => (
  <Box sx={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "1.2px", color, border: `1px solid ${color}55`, borderRadius: "999px", px: "12px", py: "5px", mb: "14px" }}>{text}</Box>
);

const FlagshipTwoPhones: React.FC = () => {
  const [inView, setInView] = useState(false);
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());
  const [etLoaded, setEtLoaded] = useState(false);
  const [etPicks, setEtPicks] = useState<number[]>([]);

  const sectionRef = useRef<HTMLDivElement>(null);
  const etFrameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    const onSrc = (e: Event) => { const next = (e as CustomEvent).detail; if (typeof next === "string" && next) { setShareSrc(next); setEtLoaded(false); setEtPicks([]); } };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  // poll the editor iframe (same-origin) for checked rows -> ordered clip ids
  useEffect(() => {
    if (!inView) return;
    const t = window.setInterval(() => {
      try {
        const doc = etFrameRef.current?.contentDocument;
        if (!doc) return;
        const checked = doc.querySelectorAll("td.checkbox .Mui-checked");
        const picks: Array<{ id: number; ord: number }> = [];
        checked.forEach((el) => {
          const tr = el.closest("tr");
          const vid = tr?.querySelector('video[id^="v-"]');
          const id = parseInt((vid?.getAttribute("id") || "").slice(2), 10);
          if (Number.isNaN(id)) return;
          let ord = picks.length + 1;
          tr?.querySelectorAll("td.checkbox span").forEach((s) => {
            const m = (s.textContent || "").trim().match(/^(\d+)(st|nd|rd|th)$/);
            if (m) ord = parseInt(m[1], 10);
          });
          picks.push({ id, ord });
        });
        picks.sort((a, b) => a.ord - b.ord);
        const ids = picks.map((p) => p.id);
        setEtPicks((prev) => (prev.join(",") === ids.join(",") ? prev : ids));
      } catch (e) { /* iframe not ready / not same-origin yet */ }
    }, 900);
    return () => window.clearInterval(t);
  }, [inView]);

  return (
    <Box ref={sectionRef} sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", justifyContent: "center", gap: { xs: "34px", md: "72px" } }}>
      {/* LEFT — the real editor */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 340 }}>
        {label("① PICK — the real editor", "#22c55e")}
        <PhoneFrame>
          {inView ? (
            <iframe
              ref={etFrameRef}
              title="CherryClipz — the real editor, mobile view"
              src={shareSrc}
              allow="fullscreen; clipboard-write"
              onLoad={() => setEtLoaded(true)}
              style={{ position: "absolute", top: 0, left: 0, width: ET_VIEWPORT_W, height: Math.round(FRAME_H / ET_SCALE), transform: `scale(${ET_SCALE})`, transformOrigin: "top left", border: "none", background: "#0b0a0f" }}
            />
          ) : null}
          {!etLoaded && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f" }}>
              <img src={logoSite} alt="" width={32} style={{ opacity: 0.85 }} />
              <Typography sx={{ fontFamily: F, fontSize: "12px", color: "#9aa0b4" }}>Loading the editor…</Typography>
            </Box>
          )}
        </PhoneFrame>
        <Typography sx={{ fontFamily: F, fontSize: "12.5px", color: "#9aa0b4", textAlign: "center", mt: "14px", maxWidth: 300 }}>
          Tick clips in your pick order — they play on the right, as one reel.
        </Typography>
      </Box>

      {/* RIGHT — the feed plays your picks */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 340 }}>
        {label("② WATCH — swipe · reframe · one reel", CHERRY)}
        <FlagshipFeedPhone heightPx={FRAME_H} pickIds={etPicks} />
        <Typography sx={{ fontFamily: F, fontSize: "12.5px", color: "#9aa0b4", textAlign: "center", mt: "14px", maxWidth: 300 }}>
          {etPicks.length ? "Playing your picks back-to-back. Drag Reframe to set the 9:16 crop." : "Swipe the reel, or pick clips on the left to stitch your own."}
        </Typography>
      </Box>
    </Box>
  );
};

export default FlagshipTwoPhones;
